import { apiClient } from '@/shared/services/api';
import { retryWithBackoff } from '@/shared/utils/retryWithBackoff';

export const getCurrentUser = async () => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.get('/users/profile');
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data;

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed fetching user';
  })

  return returnObject;
}


export const updateCurrentUser = async (formData) => {

  const returnObject = { success: false, message: '', data: null, requiresVerification: false, code: null };

  await retryWithBackoff(async () => {
    return await apiClient.put('/users/profile', formData);
  }).then((response) => {

    returnObject.success = true;
    returnObject.message = response.data.message || 'Updated successfully';

    // Check if verification is required (for email/phone updates)
    // Backend returns: { status: 200, message: '...', data: { requiresVerification: true, verificationType: 'email' } }
    const responseData = response.data.data || response.data;
    if (responseData?.requiresVerification) {
      returnObject.requiresVerification = true;
      returnObject.verificationType = responseData.verificationType;
      returnObject.data = responseData;
    } else {
      // Normal update response
      returnObject.data = responseData;
    }

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed updating user';
    returnObject.code = error?.response?.data?.code || null;
  })

  return returnObject;
}

// Verify OTP for email/phone update
export const verifyEmailPhoneUpdate = async (code) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.post('/users/verify-email-phone-update', { code });
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data;

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed verifying code';
  })

  return returnObject;
}

// Independent Email Verification

// Request email verification token
export const requestEmailVerification = async () => {
  const returnObject = { success: false, message: '', data: null, code: null, status: null };

  await retryWithBackoff(async () => {
    return await apiClient.post('/users/verify-email', {});
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data;
    returnObject.message = response.data.message || 'Email verification request successful';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.code = error?.response?.data?.code;
    returnObject.status = error?.response?.status;
    returnObject.message = error?.response?.data?.message || 'Failed to request email verification';
  });

  return returnObject;
}

// Send email verification code
export const sendEmailVerificationCode = async (otpRequestToken) => {
  const returnObject = { success: false, message: '', data: null, code: null, status: null };

  await retryWithBackoff(async () => {
    return await apiClient.post('/users/send-email-verification-code', { otpRequestToken });
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data;
    returnObject.message = response.data.message || 'Verification code sent to your email';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.code = error?.response?.data?.code;
    returnObject.status = error?.response?.status;
    returnObject.message = error?.response?.data?.message || 'Failed to send verification code';
  });

  return returnObject;
}

// Verify email code (independent verification)
export const verifyEmailCode = async (code, otpRequestToken) => {
  const returnObject = { success: false, message: '', data: null, code: null, status: null };

  await retryWithBackoff(async () => {
    return await apiClient.post('/users/verify-email-code', { code, otpRequestToken });
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data;
    returnObject.message = response.data.message || 'Email verified successfully';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.code = error?.response?.data?.code;
    returnObject.status = error?.response?.status;
    returnObject.message = error?.response?.data?.message || 'Failed to verify email code';
  });

  return returnObject;
}

// Independent Phone Verification

// Request phone verification token
export const requestPhoneVerification = async () => {
  const returnObject = { success: false, message: '', data: null, code: null, status: null };

  await retryWithBackoff(async () => {
    return await apiClient.post('/users/verify-phone', {});
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data;
    returnObject.message = response.data.message || 'Phone verification request successful';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.code = error?.response?.data?.code;
    returnObject.status = error?.response?.status;
    returnObject.message = error?.response?.data?.message || 'Failed to request phone verification';
  });

  return returnObject;
}

// Send phone verification code
export const sendPhoneVerificationCode = async (otpRequestToken) => {
  const returnObject = { success: false, message: '', data: null, code: null, status: null };

  await retryWithBackoff(async () => {
    return await apiClient.post('/users/send-phone-verification-code', { otpRequestToken });
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data;
    returnObject.message = response.data.message || 'Verification code sent to your phone';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.code = error?.response?.data?.code;
    returnObject.status = error?.response?.status;
    returnObject.message = error?.response?.data?.message || 'Failed to send verification code';
  });

  return returnObject;
}

// Verify phone code (independent verification)
export const verifyPhoneCode = async (code, otpRequestToken) => {
  const returnObject = { success: false, message: '', data: null, code: null, status: null };

  await retryWithBackoff(async () => {
    return await apiClient.post('/users/verify-phone-code', { code, otpRequestToken });
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data;
    returnObject.message = response.data.message || 'Phone verified successfully';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.code = error?.response?.data?.code;
    returnObject.status = error?.response?.status;
    returnObject.message = error?.response?.data?.message || 'Failed to verify phone code';
  });

  return returnObject;
}

// Update only specific user fields (for partial updates)
export const updateUserFields = async (fieldsToUpdate) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.put('/users/profile', fieldsToUpdate);
  }).then((response) => {

    console.log(response)
    returnObject.success = true;
    returnObject.data = response.data.data;

  }).catch((error) => {
    console.log('Failed updating user fields')
    console.log(error)
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed updating user fields';
  })

  return returnObject;
}

export const searchUsers = async (q, page = 1, limit = 10) => {

  const returnObject = { success: false, message: '', data: null };

  const params = {
    q: q,
    limit: limit,
    page: page,
    isActive: true, // Only show active users (blocked/deactivated are excluded)
  };

  await retryWithBackoff(async () => {
    return await apiClient.get('/users/search', { params });
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data.users;

  }).catch((error) => {

    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed searching for user';
  })

  return returnObject;
}

export const searchMentionTagging = async (q, entityType = 'all', limit = 10) => {
  const returnObject = { 
    success: false, 
    message: '', 
    data: { users: [], groups: [], pages: [], suggestions: [] } 
  };

  // Validate query - API doesn't accept empty queries
  const trimmedQuery = (q || '').trim();
  
  // Early return if query is empty to prevent bad request
  // For a social network with millions of users, we should not make unnecessary API calls
  if (!trimmedQuery || trimmedQuery.length === 0) {
    returnObject.message = 'Query cannot be empty';
    return returnObject;
  }
  
  // Validate query length to prevent abuse (max 100 characters)
  if (trimmedQuery.length > 100) {
    returnObject.message = 'Query too long';
    return returnObject;
  }
  
  // Use backend's max limit of 10
  const requestLimit = Math.min(limit || 10, 10);

  try {
    const response = await retryWithBackoff(async () => {
      return await apiClient.get('/users/mentions/tagging', {
        params: {
          q: trimmedQuery,
          entityType,
          limit: requestLimit
        }
      });
    });

    const responseData = response?.data?.data || {};

    const normalize = (items = [], type) => {
      return (items || []).map((item) => {
        const baseDisplayName = item.displayName || item.name || item.username || '';
        const slug = item.slug || null;
        const rawUsername = item.username || null;
        
        // Better username computation with lowercase normalization
        const computedUsername = type === 'group'
          ? (slug || rawUsername || baseDisplayName.replace(/\s+/g, '').toLowerCase())
          : (rawUsername || slug || baseDisplayName.replace(/\s+/g, '').toLowerCase());
        
        const mentionValue = computedUsername || baseDisplayName.replace(/\s+/g, '').toLowerCase();

        return {
          id: item.id,
          entityType: type,
          displayName: baseDisplayName,
          username: computedUsername,
          slug,
          imageUrl: item.imageUrl || null,
          avatar: item.imageUrl || null,
          relationship: item.relationship || null,
          role: item.role || null,
          mentionValue
        };
      });
    };

    const users = normalize(responseData.users || [], 'user');
    const groups = normalize(responseData.groups || [], 'group');
    const pages = normalize(responseData.pages || [], 'page');
    
    let suggestions = [];
    if (entityType === 'user') {
      suggestions = [...users];
    } else if (entityType === 'group') {
      suggestions = [...groups];
    } else if (entityType === 'page') {
      suggestions = [...pages];
    } else {
      suggestions = [...users, ...groups, ...pages];
    }

    returnObject.success = true;
    returnObject.data = {
      users,
      groups,
      pages,
      suggestions
    };
  } catch (error) {
    console.error('Error fetching mention suggestions:', error);
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || error?.message || 'Failed fetching mention tagging results';
    
    // Log full error for debugging in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Full error details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: '/users/mentions/tagging',
        params: { q: trimmedQuery, entityType, limit: requestLimit }
      });
    }
  }

  return returnObject;
}

export const unifiedSearch = async (q, isEnter = false, sortOption = 'tendance', page = 1, limit = 20) => {

  const returnObject = { success: false, message: '', data: null };
  if (!isEnter) {
    isEnter = null;
  }

  const params = {
    q: q,
    target: 'all',
    sort: sortOption,
    limit: limit,
    page: page,
    isEnter: isEnter
  };

  await retryWithBackoff(async () => {
    return await apiClient.get('/search/searchs', { params });
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data;

  }).catch((error) => {

    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed searching';
  })

  return returnObject;
}

export const unifiedSearchV2 = async (q, isEnter = false, sortOption = 'tendance', page = 1, target = 'all') => {
  const returnObject = { success: false, message: '', data: null };

  const params = {
    q,
    target,
    sort: sortOption,
    page,
    isEnter: isEnter || null,
  };

  await retryWithBackoff(async () => {
    return await apiClient.get('/search/v2/searchs', { params });
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data;
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed searching';
  });

  return returnObject;
};

export const getUserSuggestions = async (page = 1, limit = 20) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.get('/users/suggestions', {
      params: {
        limit: limit,
        page: page
      }
    });
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data;

  }).catch((error) => {

    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed searching for user';
  })

  return returnObject;
}


export const getUserFriend = async (page = 1, limit = 20, searchByUserId = null, notInPage = null, notInGroup = null, search = null) => {

  const returnObject = { success: false, message: '', data: null };
  const params = {
    limit: limit,
    page: page
  };
  if (searchByUserId) params.searchByUserId = searchByUserId;
  if (notInPage) params.notInPage = notInPage;
  if (notInGroup) params.notInGroup = notInGroup;
  if (search != null && String(search).trim()) params.search = String(search).trim();

  // Do not retry on 503 to avoid amplifying load when the server is overloaded
  await retryWithBackoff(async () => {
    return await apiClient.get('/friendship/friends', { params });
  }, {
    shouldRetry: (error) => {
      const status = error?.response?.status;
      if (status === 503) return false;
      const isNetworkError = !error.response && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.message?.includes('Network'));
      return status === 429 || isNetworkError;
    }
  }).then((response) => {

    // Set button state based on friendship status
    // The /friendship/friends endpoint only returns friends with FRIEND status
    // So all friends in this list should show "unfriend" button
    response.data.friends.forEach((user) => {
      // If friendship data is available, use it to determine button state
      if (user.friendship) {
        const status = user.friendship.status;
        const type = user.friendship.type;
        
        if (status === 'FRIEND' && type === 'FRIEND') {
          user.friend.friendShipButtonState = 'unfriend'; // Show "Unfriend" button for friends
        } else if (status === 'PENDING' && type === 'FRIEND') {
          user.friend.friendShipButtonState = 'cancelRequest'; // Show "Cancel" button for pending requests
        } else if (status === 'REMOVED' || status === 'DECLINED') {
          user.friend.friendShipButtonState = 'sendRequest'; // Show "Add friend" button for removed/declined
        } else {
          user.friend.friendShipButtonState = 'unfriend'; // Default to unfriend for friends list
        }
      } else {
        // Default: if in friends list, they are friends, so show unfriend button
        user.friend.friendShipButtonState = 'unfriend';
      }
    })

    returnObject.success = true;
    returnObject.data = response.data.friends;
    
    // Include pagination information if available
    if (response.data.pagination) {
      returnObject.pagination = response.data.pagination;
    }

  }).catch((error) => {

    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed getting friend list';
  })

  return returnObject;
}

export const getSpecificUserFriends = async (id) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.get(`/users/${id}/friends`);
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data;

  }).catch((error) => {

    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed getting friend list';
  })

  return returnObject;
}

export const getUserFriendsCount = async (id) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.get('/users/friends/count', {
      params: {
        id: id
      }
    });
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.count;

  }).catch((error) => {

    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed getting friend list';
  })

  return returnObject;
}


export const getUserProfile = async (id) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.get(`/users/profile/${id}`);
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data;

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed fetching user';
  })

  return returnObject;
}


export const getUserFriendsWithFamilyInfomrations = async () => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.get(`/users/friends/with-family-details`);
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data;

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed fetching user';
  })

  return returnObject;
}

export const getSearchHistory = async () => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.get('/search/history');
  }).then((response) => {

    returnObject.success = true;
    // Handle both response.data.data and response.data structures
    returnObject.data = response.data.data || response.data;

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed fetching search history';
  })

  return returnObject;
}

export const createSearchHistory = async (searchData) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.post('/search/history', searchData);
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data || response.data;

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed creating search history';
  })

  return returnObject;
}

export const deleteAllSearchHistory = async () => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.delete('/search/history');
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data || response.data;

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed deleting all search history';
  })

  return returnObject;
}

export const deleteSearchHistoryItem = async (id) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.delete(`/search/history/${id}`);
  }).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data.data || response.data;

  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed deleting search history item';
  })

  return returnObject;
}